import { createContext, useContext } from "react";
import type { ComponentType, ReactNode } from "react";

/** One entry of the recharts tooltip payload, as injected into a custom tooltip content. */
export interface ChartTooltipPayloadItem {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
  payload?: unknown;
}

interface ChartTooltipContextValue {
  payload: ChartTooltipPayloadItem[];
  label: unknown;
}

// Contexts live apart from the shell/parts (the ItemContext.ts precedent): ChartTooltipContent
// imports the parts for its default composition while the parts need these contexts — co-locating
// either would create an import cycle.
export const ChartTooltipContext = createContext<ChartTooltipContextValue | null>(null);

export const useChartTooltipContext = (): ChartTooltipContextValue => {
  const context = useContext(ChartTooltipContext);
  if (!context) {
    throw new Error("Chart tooltip parts must be used within <Chart.TooltipContent />");
  }
  return context;
};

export interface ChartTooltipRowContextValue {
  item: ChartTooltipPayloadItem;
  /** Config-resolved series label (falls back to the raw payload name). */
  name: ReactNode;
  value?: number | string;
  indicatorColor?: string;
  icon?: ComponentType;
}

export const ChartTooltipRowContext = createContext<ChartTooltipRowContextValue | null>(null);

export const useChartTooltipRowContext = (): ChartTooltipRowContextValue => {
  const context = useContext(ChartTooltipRowContext);
  if (!context) {
    throw new Error("Chart tooltip row parts must be used within <Chart.TooltipRows />");
  }
  return context;
};
